import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { api, eur, stationLabel } from "@/lib/api";
import { readCart, addItem, removeItem, updateItemQty, cartTotals } from "@/lib/cart";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ShoppingBag, Plus, Minus, X, Bell, ArrowRight, Search } from "lucide-react";
import { toast } from "sonner";
import SuggestionCards from "@/components/SuggestionCards";
import { MenuSkeleton } from "@/components/skeletons/Skeletons";
import haptic from "@/lib/haptic";
import WelcomeOverlay from "@/components/WelcomeOverlay";

// Accent-insensitive, case-insensitive normalisation for search matching.
const norm = (s) => (s || "")
  .toString()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .trim();

export default function Menu() {
  const [menu, setMenu] = useState(null);
  const [settings, setSettings] = useState(null);
  const [cart, setCart] = useState(readCart());
  const [activeCat, setActiveCat] = useState(null);
  const [product, setProduct] = useState(null);
  const [search, setSearch] = useState("");
  // Phase 9 — hierarchical drill-down state (only used for roots with
  // `hierarchical_customer_nav=true`). Flat roots keep the legacy inline UI.
  const [navPath, setNavPath] = useState({ rootId: null, subId: null });
  const nav = useNavigate();
  const location = useLocation();

  // Sprint A · Silent Welcome — mostra l'overlay una sola volta quando
  // Menu.jsx viene raggiunto da OrderStation dopo un QR scan (state.welcome).
  // Refresh o navigazione manuale non lo mostrano (clear via replaceState).
  const [showWelcome, setShowWelcome] = useState(() => !!location.state?.welcome);
  useEffect(() => {
    if (location.state?.welcome) {
      try { window.history.replaceState({}, "", location.pathname); } catch { /* noop */ }
    }
  }, [location.pathname, location.state]);

  // Scroll to top whenever the drill-down changes level (mobile-first UX).
  useEffect(() => {
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  }, [navPath.rootId, navPath.subId]);

  useEffect(() => {
    const handler = () => setCart(readCart());
    window.addEventListener("sb-cart-change", handler);
    return () => window.removeEventListener("sb-cart-change", handler);
  }, []);

  useEffect(() => {
    api.get("/public/menu").then((r) => {
      setMenu(r.data);
      if (r.data.categories.length) setActiveCat(r.data.categories[0].id);
    });
    api.get("/public/settings").then((r) => setSettings(r.data));
    if (!readCart().station) nav("/");
  }, [nav]);

  const productsByCat = useMemo(() => {
    const map = {};
    (menu?.products || []).forEach((p) => {
      (map[p.category_id] = map[p.category_id] || []).push(p);
    });
    return map;
  }, [menu]);

  const catById = useMemo(() => {
    const map = {};
    (menu?.categories || []).forEach((c) => { map[c.id] = c; });
    return map;
  }, [menu]);

  const searchResults = useMemo(() => {
    const q = norm(search);
    if (!q || !menu) return null;
    const terms = q.split(/\s+/).filter(Boolean);
    return menu.products.filter((p) => {
      // Include every wine metadata field so "Bellavista" or "Franciacorta"
      // find the wine even if it's not in the product name. Also index
      // badges (Best Seller, Premium, Rosé, Bio, Consigliato) and any
      // key/value pair from the open `extra` dict so searching e.g.
      // "annata 2018" surfaces matching bottles.
      let wineHay = "";
      if (p.wine) {
        const wCopy = { ...p.wine };
        const extra = wCopy.extra || {};
        delete wCopy.extra;
        wineHay = [
          ...Object.values(wCopy).filter(Boolean),
          ...Object.keys(extra),
          ...Object.values(extra).filter(Boolean),
        ].join(" ");
      }
      const hay = norm([
        p.name,
        p.description,
        (p.tags || []).join(" "),
        (p.allergens || []).join(" "),
        (p.badges || []).join(" "),
        wineHay,
        catById[p.category_id]?.name || "",
      ].join(" "));
      return terms.every((t) => hay.includes(t));
    });
  }, [search, menu, catById]);

  const categoryByProductId = useMemo(() => {
    const map = {};
    (menu?.products || []).forEach((p) => { map[p.id] = p.category_id; });
    return map;
  }, [menu]);
  const copertoCategoryIds = useMemo(() => (menu?.categories || []).filter((c) => c.requires_coperto).map((c) => c.id), [menu]);

  const totals = cartTotals(cart, {
    serviceChargeCents: settings?.service_charge_cents || 0,
    copertoCents: settings?.coperto_cents || 0,
    peopleCount: 1,
    copertoCategoryIds,
    categoryByProductId,
  });

  if (!menu) return <MenuSkeleton />;

  return (
    <>
      {showWelcome && (
        <WelcomeOverlay
          stationLabel={stationLabel(cart.station)}
          onDone={() => setShowWelcome(false)}
        />
      )}
    <div className="min-h-screen pb-32">
      <header className="sticky top-0 z-20 bg-sand-100/95 backdrop-blur-xl border-b border-sand-300 px-5 py-4">
        <div className="pr-14">
          <div className="eyebrow">Postazione</div>
          <div data-testid="menu-station-label" className="font-serif text-xl leading-none">{stationLabel(cart.station)}</div>
          <Link to="/" data-testid="change-station-link" className="mt-1 inline-block text-xs text-bronze-600 hover:text-bronze-700 underline underline-offset-2">
            Cambia postazione
          </Link>
        </div>

        <div className="mt-3 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none" />
          <Input
            data-testid="menu-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca nel menu (es. spritz, tonno, senza glutine)…"
            className="pl-9 pr-9 rounded-full bg-white border-sand-300"
          />
          {search && (
            <button
              data-testid="menu-search-clear"
              onClick={() => setSearch("")}
              aria-label="Pulisci ricerca"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-sand-200 flex items-center justify-center text-ink-500"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {!search && (
          <div className="mt-3 -mx-5 px-5 overflow-x-auto flex gap-2 scrollbar-none">
            {menu.categories.filter((c) => !c.parent_id).map((c) => {
              // A root triggers drill-down when EITHER the explicit hier flag is
              // on OR it has any children in the payload. The payload-derived
              // check heals stale snapshots where a category was made
              // hierarchical (with subs) but the flag itself wasn't published.
              // Without this fallback, non-hier roots that own subs become
              // orphaned: subs exist in data but have nowhere to render (bug
              // reintrodotto dal fix Fase 10.1).
              const hasKids = (menu.categories || []).some((x) => x.parent_id === c.id);
              const isFolder = !!c.hierarchical_customer_nav || hasKids;
              return (
                <button
                  key={c.id}
                  data-testid={`cat-${c.slug || c.id}`}
                  onClick={() => {
                    // Header tab bar shows ONLY root categories. Click behaviour:
                    // - Folder root (hier flag OR has kids) → drill into L1.
                    // - Truly flat root → scroll to its inline section (L0).
                    if (isFolder) {
                      setNavPath({ rootId: c.id, subId: null });
                      setActiveCat(c.id);
                      return;
                    }
                    setNavPath({ rootId: null, subId: null });
                    setActiveCat(c.id);
                    document.getElementById(`sec-${c.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm ${activeCat === c.id ? "border-bronze-600 bg-bronze-600 text-white" : "border-sand-300 bg-white"}`}
                >
                  {c.name}{isFolder ? " ›" : ""}
                </button>
              );
            })}
          </div>
        )}
      </header>

      <main className="px-5 mt-4 space-y-8">
        {searchResults !== null ? (
          <section data-testid="menu-search-results">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="text-3xl">Risultati</h2>
              <span data-testid="menu-search-count" className="text-sm text-ink-500">
                {searchResults.length} {searchResults.length === 1 ? "prodotto" : "prodotti"}
              </span>
            </div>
            {searchResults.length === 0 ? (
              <div data-testid="menu-search-empty" className="card-elevated p-6 text-center">
                <div className="font-serif text-xl">Niente da servire per “{search}”</div>
                <div className="text-sm text-ink-500 mt-1">Prova con un altro termine o sfoglia le categorie.</div>
                <button
                  data-testid="menu-search-reset"
                  onClick={() => setSearch("")}
                  className="mt-4 inline-flex items-center gap-1 rounded-full border border-bronze-600 text-bronze-600 px-4 py-2 text-sm hover:bg-bronze-600 hover:text-white transition-colors"
                >
                  Pulisci ricerca
                </button>
              </div>
            ) : (
              <div className="grid gap-3">
                {searchResults.map((p) => {
                  const isWine = catById[p.category_id]?.display_type === "wine_list";
                  return (
                    <button
                      key={p.id}
                      data-testid={`product-${p.id}`}
                      onClick={() => isWine ? nav(`/wine/${p.id}`) : setProduct(p)}
                      className="card-elevated p-4 text-left flex justify-between gap-4 hover:border-bronze-600 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="eyebrow text-bronze-600">{catById[p.category_id]?.name}</div>
                        <div className="font-medium mt-1">
                          {p.wine?.producer ? <span className="uppercase tracking-wide text-xs text-bronze-700 mr-1">{p.wine.producer} —</span> : null}
                          {p.name}
                        </div>
                        {p.description && <div className="text-sm text-ink-500 mt-1">{p.description}</div>}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {(p.badges || []).map((b) => <span key={b} className="chip border-bronze-500/60 text-bronze-700 bg-bronze-50">{b}</span>)}
                          {p.tags.map((t) => <span key={t} className="chip">{t}</span>)}
                          {p.allergens.length > 0 && <span className="chip text-bronze-600 border-bronze-500/40">allergeni: {p.allergens.join(", ")}</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-serif text-lg">{eur(p.price_cents)}</div>
                        {!isWine && <div className="text-xs text-bronze-600 mt-2">Aggiungi +</div>}
                        {p.available === false && <div className="text-xs text-red-700 mt-2">Esaurito</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        ) : (() => {
          // Phase 9 hierarchical navigation.
          const renderProds = (catId) => (productsByCat[catId] || []).map((p) => (
            <button
              key={p.id}
              data-testid={`product-${p.id}`}
              onClick={() => setProduct(p)}
              className="card-elevated p-4 text-left flex justify-between gap-4 hover:border-bronze-600 transition-colors"
            >
              <div className="flex-1">
                <div className="font-medium">{p.name}</div>
                {p.description && <div className="text-sm text-ink-500 mt-1">{p.description}</div>}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {p.tags.map((t) => <span key={t} className="chip">{t}</span>)}
                  {p.allergens.length > 0 && <span className="chip text-bronze-600 border-bronze-500/40">allergeni: {p.allergens.join(", ")}</span>}
                </div>
              </div>
              <div className="text-right">
                <div className="font-serif text-lg">{eur(p.price_cents)}</div>
                <div className="text-xs text-bronze-600 mt-2">Aggiungi +</div>
              </div>
            </button>
          ));

          // Level 2: showing products of a sub (drill-down leaf).
          if (navPath.subId) {
            const sub = menu.categories.find((c) => c.id === navPath.subId);
            const root = menu.categories.find((c) => c.id === navPath.rootId);
            if (!sub) { setNavPath({ rootId: null, subId: null }); return null; }
            // Phase 11.2 — wine-specific sort_order overrides the default
            // product-level sort inside a wine_list category. Nulls / zeros
            // fall back to product.sort_order, then to name.
            const isWineList = sub.display_type === "wine_list";
            const subProds = isWineList
              ? (productsByCat[sub.id] || []).slice().sort((a, b) => {
                  const sa = (a.wine && Number.isFinite(a.wine.sort_order)) ? a.wine.sort_order : 0;
                  const sb = (b.wine && Number.isFinite(b.wine.sort_order)) ? b.wine.sort_order : 0;
                  if (sa !== sb) return sa - sb;
                  return (a.sort_order || 0) - (b.sort_order || 0)
                      || (a.name || "").localeCompare(b.name || "");
                })
              : (productsByCat[sub.id] || []);
            return (
              <section data-testid={`cat-section-${sub.id}`}>
                <button data-testid="hier-back"
                        onClick={() => setNavPath({ rootId: root?.id || null, subId: null })}
                        className="mb-2 inline-flex items-center gap-2 text-sm rounded-full border border-sand-300 px-3 py-1.5 bg-white hover:bg-sand-100">
                  ← {root ? root.name : "Torna alle categorie"}
                </button>
                <div className="text-xs text-ink-500 mb-1" data-testid="hier-breadcrumb">
                  <span className="cursor-pointer hover:underline" onClick={() => setNavPath({ rootId: null, subId: null })}>Menu</span>
                  {root && <> <span className="mx-1">›</span> <span className="cursor-pointer hover:underline" onClick={() => setNavPath({ rootId: root.id, subId: null })}>{root.name}</span></>}
                  <span className="mx-1">›</span> <b>{sub.name}</b>
                </div>
                <h2 className={`${isWineList ? "font-serif text-4xl mb-6" : "text-3xl mb-3"}`}>{sub.name}</h2>
                {isWineList ? (
                  <div className="divide-y divide-sand-300" data-testid="wine-list">
                    {subProds.map((p) => <WineListRow key={p.id} product={p} onClick={() => nav(`/wine/${p.id}`)} />)}
                  </div>
                ) : (
                  <div className="grid gap-3">{renderProds(sub.id)}</div>
                )}
                {subProds.length === 0 && <div className="text-ink-500 text-sm">Nessun prodotto disponibile</div>}
              </section>
            );
          }

          // Level 1: showing subs of a hierarchical root as cards.
          if (navPath.rootId) {
            const root = menu.categories.find((c) => c.id === navPath.rootId);
            if (!root) { setNavPath({ rootId: null, subId: null }); return null; }
            const allKids = menu.categories.filter((x) => x.parent_id === root.id);
            // Only show subs that actually have visible products at this moment
            // (hide sottocategoria vuota / fuori disponibilità).
            const kids = allKids.filter((s) => (productsByCat[s.id] || []).length > 0);
            const rootOwn = productsByCat[root.id] || [];
            const hasOwn = rootOwn.length > 0;
            return (
              <section data-testid={`cat-section-${root.id}`}>
                <button data-testid="hier-back"
                        onClick={() => setNavPath({ rootId: null, subId: null })}
                        className="mb-2 inline-flex items-center gap-2 text-sm rounded-full border border-sand-300 px-3 py-1.5 bg-white hover:bg-sand-100">
                  ← Torna alle categorie
                </button>
                <div className="text-xs text-ink-500 mb-1" data-testid="hier-breadcrumb">
                  <span className="cursor-pointer hover:underline" onClick={() => setNavPath({ rootId: null, subId: null })}>Menu</span>
                  <span className="mx-1">›</span> <b>{root.name}</b>
                </div>
                <h2 className="text-3xl mb-4">{root.name}</h2>
                <div className="grid gap-3" data-testid="hier-sub-list">
                  {hasOwn && (
                    <button data-testid={`hier-sub-card-__root__`}
                            onClick={() => setNavPath({ rootId: root.id, subId: root.id })}
                            className="card-elevated p-5 text-left flex justify-between items-center hover:border-bronze-600 transition-colors">
                      <div>
                        <div className="font-serif text-2xl">Altro</div>
                        <div className="text-xs text-ink-500 mt-1">{rootOwn.length} prodotti diretti</div>
                      </div>
                      <div className="text-bronze-600 text-2xl">›</div>
                    </button>
                  )}
                  {kids.map((sub) => {
                    const count = (productsByCat[sub.id] || []).length;
                    return (
                      <button key={sub.id}
                              data-testid={`hier-sub-card-${sub.id}`}
                              onClick={() => setNavPath({ rootId: root.id, subId: sub.id })}
                              className="card-elevated p-5 text-left flex justify-between items-center hover:border-bronze-600 transition-colors">
                        <div>
                          <div className="font-serif text-2xl">{sub.name}</div>
                          <div className="text-xs text-ink-500 mt-1">{count} prodotti</div>
                        </div>
                        <div className="text-bronze-600 text-2xl">›</div>
                      </button>
                    );
                  })}
                  {kids.length === 0 && !hasOwn && <div className="text-ink-500 text-sm">Nessuna sottocategoria disponibile in {root.name}</div>}
                </div>
              </section>
            );
          }

          // Level 0: root categories only.
          //
          // Regola di visualizzazione (fix regressione Fase 10.1):
          // - Root con visibleChildren > 0 (a prescindere dal flag hier) →
          //   folder card (drill-down). Il flag hier resta un opt-in esplicito,
          //   ma NON è più una gate: se una root ha figli visibili, la si tratta
          //   sempre come cartella. Questo evita che le sottocategorie diventino
          //   orfane quando lo snapshot pubblicato ha hier=OFF ma nel frattempo
          //   sono state create/pubblicate sottocategorie con prodotti.
          // - Root senza sub visibili ma con prodotti diretti → inline section.
          // - Root senza prodotti diretti e senza sub visibili → nascosta.
          return menu.categories.filter((c) => !c.parent_id).map((c) => {
            const children = menu.categories.filter((x) => x.parent_id === c.id);
            const visibleChildren = children.filter((s) => (productsByCat[s.id] || []).length > 0);
            const ownProds = productsByCat[c.id] || [];

            // Folder card — auto-drill se ci sono sottocategorie con prodotti,
            // indipendentemente dal flag hierarchical_customer_nav.
            if (visibleChildren.length > 0) {
              const totalProds = ownProds.length + visibleChildren.reduce((n, s) => n + (productsByCat[s.id] || []).length, 0);
              return (
                <button key={c.id} id={`sec-${c.id}`}
                        data-testid={`hier-root-card-${c.id}`}
                        onClick={() => setNavPath({ rootId: c.id, subId: null })}
                        className="w-full card-elevated p-6 text-left flex justify-between items-center hover:border-bronze-600 transition-colors">
                  <div>
                    <div className="font-serif text-3xl">{c.name}</div>
                    <div className="text-sm text-ink-500 mt-1">
                      {visibleChildren.length} sottocategorie · {totalProds} prodotti
                    </div>
                  </div>
                  <div className="text-bronze-600 text-3xl">›</div>
                </button>
              );
            }

            // Inline flat rendering. Sub-categories are NEVER concatenated as
            // H3 inline. If root has zero direct products AND no visible subs,
            // hide it entirely.
            if (ownProds.length === 0) return null;

            return (
              <section key={c.id} id={`sec-${c.id}`} data-testid={`cat-section-${c.id}`}>
                <h2 className="text-3xl mb-3">{c.name}</h2>
                <div className="grid gap-3">{renderProds(c.id)}</div>
              </section>
            );
          });
        })()}
      </main>

      <ProductSheet product={product} onClose={() => setProduct(null)} modifierGroups={menu.modifier_groups} />

      {cart.items.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 glass-cart px-5 py-4 z-30">
          <Sheet>
            <SheetTrigger asChild>
              <button data-testid="open-cart-btn" className="w-full flex items-center justify-between rounded-full bg-bronze-600 text-white px-5 py-4">
                <span className="flex items-center gap-2 font-medium"><ShoppingBag className="w-5 h-5" /> {cart.items.reduce((n, i) => n + i.quantity, 0)} nel carrello</span>
                <span className="font-serif text-lg">{eur(totals.total)}</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto">
              <SheetHeader><SheetTitle>Il tuo carrello</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-3">
                {cart.items.map((i) => {
                  const unit = i.unit_price_cents + i.modifiers.reduce((a, m) => a + (m.price_delta_cents || 0), 0);
                  return (
                    <div key={i.uid} className="flex items-start gap-3 border-b border-sand-300 pb-3">
                      <div className="flex-1">
                        <div className="font-medium">{i.product_name}</div>
                        {i.modifiers.map((m, idx) => <div key={idx} className="text-xs text-ink-500">{m.group_name}: {m.option_name}</div>)}
                        {i.note && <div className="text-xs text-ink-500 italic">Nota: {i.note}</div>}
                        <div className="mt-2 inline-flex items-center gap-2">
                          <button data-testid={`cart-dec-${i.uid}`} onClick={() => updateItemQty(i.uid, i.quantity - 1)} className="w-8 h-8 rounded-full border border-sand-300 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                          <span className="w-6 text-center">{i.quantity}</span>
                          <button data-testid={`cart-inc-${i.uid}`} onClick={() => updateItemQty(i.uid, i.quantity + 1)} className="w-8 h-8 rounded-full border border-sand-300 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-serif">{eur(unit * i.quantity)}</div>
                        <button data-testid={`cart-remove-${i.uid}`} onClick={() => removeItem(i.uid)} className="mt-2 text-xs text-ink-500 hover:text-bronze-600"><X className="w-4 h-4 inline" /></button>
                      </div>
                    </div>
                  );
                })}
                <div className="pt-2 space-y-1">
                  <div className="flex justify-between text-sm"><span>Subtotale</span><span>{eur(totals.subtotal)}</span></div>
                  {totals.service > 0 && <div className="flex justify-between text-sm"><span>Service consegna</span><span>{eur(totals.service)}</span></div>}
                  {totals.needsCoperto && <div className="flex justify-between text-sm text-ink-500"><span>Coperto ({eur(totals.copertoUnit)}/persona) — indica al checkout</span><span></span></div>}
                  <div className="flex justify-between font-serif text-xl pt-2"><span>Totale</span><span>{eur(totals.total)}</span></div>
                </div>
                <SuggestionCards variant="cart"
                                 excludeIds={cart.items.map((i) => i.product_id)} />
                <Button data-testid="go-checkout-btn" className="w-full mt-3 rounded-full" onClick={() => nav("/checkout")}>
                  Procedi al checkout <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      )}
    </div>
    </>
  );
}

// Phase 11 — Wine list row. Restaurant-menu style, no ecommerce feel: no
// "Aggiungi +" button, no card border. Producer + wine name + region •
// grape on the left, dotted leader, price on the right. The full row is
// clickable and routes to /wine/:id.
//
// Phase 11.2 — respects wine.featured (subtle bronze border + ★ icon) and
// wine.show_price (renders "Su richiesta" instead of price). Sold-out
// still wins over both.
//
// Phase 11.3 — leading thumbnail (uses `image_thumb_url` for a lean
// payload, falls back to `image_url`, and finally to the "Sarabi"
// placeholder). `loading="lazy"` + `decoding="async"` keep the list snappy
// even with many bottles.
function WineListRow({ product, onClick }) {
  const w = product.wine || {};
  const badges = product.badges || [];
  const available = product.available !== false;
  const featured = !!w.featured;
  const showPrice = w.show_price !== false;
  const region = w.regione || w.provenienza;
  const secondary = [region, w.vitigno].filter(Boolean).join(" · ");
  const thumb = product.image_thumb_url || product.image_url || null;
  return (
    <button
      data-testid={`wine-row-${product.id}`}
      data-featured={featured || undefined}
      onClick={onClick}
      className={`w-full text-left py-6 md:py-7 px-4 md:px-6 flex items-start gap-5 md:gap-7 hover:bg-white/60 transition-colors group border-b border-sand-200/60 last:border-b-0 ${featured ? "bg-bronze-50/30" : ""}`}
    >
      <div className="shrink-0 w-16 h-24 md:w-20 md:h-28 overflow-hidden bg-white flex items-center justify-center shadow-[0_10px_30px_-15px_rgba(90,60,30,0.35)]"
           data-testid={`wine-row-thumb-${product.id}`}>
        {thumb ? (
          <img src={thumb} alt={product.name} loading="lazy" decoding="async"
               className="w-full h-full object-contain" />
        ) : (
          <span className="italic text-ink-300 font-serif text-xs tracking-widest">Sarabi</span>
        )}
      </div>
      <div className="flex-1 min-w-0 pt-1">
        {w.producer && (
          <div className="uppercase text-[10px] tracking-[0.28em] text-bronze-700 font-medium truncate flex items-center gap-1.5">
            {featured && <span aria-hidden="true" className="text-bronze-600">★</span>}
            {w.producer}
          </div>
        )}
        <div className="font-serif text-xl md:text-2xl leading-[1.15] mt-1.5 text-ink-900">
          {product.name}
        </div>
        {w.denominazione && (
          <div className="text-sm text-ink-700 italic mt-1">{w.denominazione}</div>
        )}
        {secondary && (
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink-500 mt-1.5">{secondary}</div>
        )}
        {badges.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {badges.map((b) => (
              <span key={b} className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border border-bronze-500/40 text-bronze-700 bg-bronze-50/40 rounded-sm">{b}</span>
            ))}
          </div>
        )}
      </div>
      <div className="shrink-0 text-right pt-1.5 min-w-[5rem]">
        {!available ? (
          <div className="text-[9px] uppercase tracking-[0.22em] px-2 py-1 border border-red-300 text-red-700 bg-red-50 rounded-sm">Esaurito</div>
        ) : showPrice ? (
          <div className="font-serif text-xl md:text-2xl text-ink-900">{eur(product.price_cents)}</div>
        ) : (
          <div className="italic text-ink-500 text-xs uppercase tracking-[0.18em]" data-testid={`wine-price-on-request-${product.id}`}>Su richiesta</div>
        )}
      </div>
    </button>
  );
}

function ProductSheet({ product, modifierGroups, onClose }) {
  const [selections, setSelections] = useState({});
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");

  // Groups attached to this product, in admin-configured order.
  const groups = useMemo(() => (
    (product?.modifier_group_ids || [])
      .map((mid) => modifierGroups.find((m) => m.id === mid))
      .filter(Boolean)
      // Phase 14 — hide inactive groups from customers (retro-compat: legacy
      // groups without the `active` field default to true).
      .filter((g) => g.active !== false)
      .map((g) => ({
        ...g,
        // Options: honour sort_order + hide those explicitly deactivated
        // stays visible-but-disabled (see render below) so users understand
        // why a choice is momentarily unavailable.
        options: [...(g.options || [])].sort(
          (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
        ),
      }))
  ), [product?.id, modifierGroups]);

  // Seed selections with default_selected options each time the product changes.
  useEffect(() => {
    if (!product) return;
    const seed = {};
    for (const g of groups) {
      const defs = g.options.filter((o) => o.default_selected && o.active !== false);
      if (defs.length === 0) continue;
      if (!g.multi) {
        const o = defs[0];
        seed[g.id] = [{ group_id: g.id, group_name: g.name,
                        option_id: o.id, option_name: o.name,
                        price_delta_cents: o.price_delta_cents }];
      } else {
        const max = g.max_select || defs.length;
        const kept = defs.slice(0, max);
        seed[g.id] = kept.map((o) => ({ group_id: g.id, group_name: g.name,
                                        option_id: o.id, option_name: o.name,
                                        price_delta_cents: o.price_delta_cents }));
      }
    }
    setSelections(seed); setQty(1); setNote("");
  }, [product?.id, groups]);

  if (!product) return null;

  const errors = groups.reduce((acc, g) => {
    const cnt = (selections[g.id] || []).length;
    const minSel = g.required ? Math.max(1, g.min_select || 0) : (g.min_select || 0);
    if (cnt < minSel) {
      acc[g.id] = g.multi
        ? `Seleziona almeno ${minSel} opzion${minSel === 1 ? "e" : "i"}`
        : "Seleziona un'opzione";
    }
    return acc;
  }, {});
  const canAdd = Object.keys(errors).length === 0;

  const toggle = (g, opt) => {
    if (opt.active === false) return;      // disabled → not clickable
    setSelections((s) => {
      const cur = s[g.id] || [];
      if (g.multi) {
        const exists = cur.find((o) => o.option_id === opt.id);
        if (exists) return { ...s, [g.id]: cur.filter((o) => o.option_id !== opt.id) };
        const cap = g.max_select || Infinity;
        if (cur.length >= cap) {
          toast.message(`Hai già scelto ${cap} opzion${cap === 1 ? "e" : "i"}`);
          return s;
        }
        return { ...s, [g.id]: [...cur, { group_id: g.id, group_name: g.name,
                                          option_id: opt.id, option_name: opt.name,
                                          price_delta_cents: opt.price_delta_cents }] };
      }
      // radio
      return { ...s, [g.id]: [{ group_id: g.id, group_name: g.name,
                                option_id: opt.id, option_name: opt.name,
                                price_delta_cents: opt.price_delta_cents }] };
    });
  };

  const submit = () => {
    if (!canAdd) return;
    const mods = Object.values(selections).flat();
    addItem(product, qty, mods, note.trim() || null);
    haptic.tap();
    onClose();
    toast.success(`${product.name} aggiunto al carrello`);
  };

  const totalDelta = Object.values(selections).flat()
    .reduce((a, m) => a + (m.price_delta_cents || 0), 0);

  return (
    <Sheet open={!!product} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl">{product.name}</SheetTitle>
          {product.description && <p className="text-sm text-ink-500">{product.description}</p>}
        </SheetHeader>
        <div className="mt-4 space-y-4">
          {groups.map((g) => (
            <div key={g.id} data-testid={`mgroup-block-${g.id}`}>
              <div className="flex items-baseline justify-between">
                <div className="font-medium">{g.name}</div>
                <div className="text-xs text-ink-500">
                  {g.required ? "Obbligatorio" : "Facoltativo"}
                  {g.multi ? ` · ${g.min_select || 0}–${g.max_select || "∞"}` : ""}
                </div>
              </div>
              {g.customer_description && (
                <div className="text-xs text-ink-500 mt-0.5">{g.customer_description}</div>
              )}
              <div className="mt-2 grid gap-2">
                {g.options.map((opt) => {
                  const sel = (selections[g.id] || []).some((o) => o.option_id === opt.id);
                  const disabled = opt.active === false;
                  return (
                    <button key={opt.id}
                      data-testid={`mod-${g.id}-${opt.id}`}
                      onClick={() => toggle(g, opt)}
                      disabled={disabled}
                      className={`text-left rounded-2xl border px-4 py-3 flex justify-between items-center gap-2
                                  ${sel ? "border-bronze-600 bg-sand-100" : "border-sand-300"}
                                  ${disabled ? "opacity-50 cursor-not-allowed line-through" : ""}`}>
                      <span className="flex items-center gap-2 flex-wrap">
                        <span>{opt.name}</span>
                        {(opt.badges || []).map((b) => (
                          <span key={b}
                                className="text-[10px] uppercase tracking-wider rounded-full bg-bronze-50 text-bronze-700 border border-bronze-200 px-1.5 py-0.5">
                            {b}
                          </span>
                        ))}
                        {disabled && (
                          <span className="text-[10px] uppercase tracking-wider rounded-full bg-ink-100 text-ink-600 px-1.5 py-0.5">
                            esaurito
                          </span>
                        )}
                      </span>
                      {opt.price_delta_cents > 0 && !disabled && (
                        <span className="text-sm text-bronze-600 shrink-0">+{eur(opt.price_delta_cents)}</span>
                      )}
                    </button>
                  );
                })}
                {g.options.length === 0 && (
                  <div className="text-xs text-ink-500 italic">Nessuna opzione disponibile.</div>
                )}
              </div>
              {errors[g.id] && (
                <div className="text-xs text-red-600 mt-1" data-testid={`mgroup-err-${g.id}`}>
                  {errors[g.id]}
                </div>
              )}
            </div>
          ))}
          <div>
            <div className="font-medium mb-1">Note</div>
            <Textarea data-testid="product-note" placeholder="Es. senza sale…" maxLength={140} value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="text-[11px] text-ink-500 mt-1">Le note non sostituiscono la gestione degli allergeni.</div>
          </div>
          <SuggestionCards productId={product.id} variant="product"
                           excludeIds={[product.id]} />
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-2">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-9 h-9 rounded-full border border-sand-300"><Minus className="w-4 h-4 mx-auto" /></button>
              <span className="w-6 text-center">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="w-9 h-9 rounded-full border border-sand-300"><Plus className="w-4 h-4 mx-auto" /></button>
            </div>
            <Button data-testid="add-to-cart-btn" disabled={!canAdd} className="flex-1 rounded-full" onClick={submit}>
              Aggiungi · {eur((product.price_cents + totalDelta) * qty)}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
